import { Client } from '@/shared/types';
import { supabaseService } from '@/shared/services/supabaseService';
import { authService } from '@/shared/services/realData/auth';

/**
 * Client CRUD operations
 * Handles all client-related database operations
 */
export const clientService = {
  /**
   * Get all clients for the current user
   */
  async getClients(): Promise<Client[]> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    // Use the client service with the proper Supabase UUID
    const { getClients } = await import('@/shared/services/services/clientService');
    return getClients(supabaseUserId);
  },

  /**
   * Add a new client
   */
  async addClient(client: Omit<Client, 'id'>): Promise<void> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    // Use the client service with the proper Supabase UUID
    const { createClient } = await import('@/shared/services/services/clientService');
    await createClient(supabaseUserId, client);
  },

  /**
   * Update an existing client
   */
  async updateClient(clientId: string, updates: Partial<Client>): Promise<void> {
    // Use the client service
    const { updateClient } = await import('@/shared/services/services/clientService');
    await updateClient(clientId, updates);
  },

  /**
   * Delete a client
   */
  async deleteClient(clientId: string): Promise<void> {
    // Use the client service
    const { deleteClient } = await import('@/shared/services/services/clientService');
    await deleteClient(clientId);
  },

  /**
   * Get a client by ID
   */
  async getClientById(clientId: string): Promise<Client | null> {
    const { data, error } = await supabaseService.client
      .from('clients')
      .select('*')
      .eq('id', clientId)
      .single();

    if (error) return null;
    return data;
  },

  /**
   * Create a new client (alias for addClient)
   */
  async createClient(clientData: Partial<Client>): Promise<Client> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    // Use the client service with the proper Supabase UUID
    const { createClient } = await import('@/shared/services/services/clientService');
    return createClient(supabaseUserId, clientData as Omit<Client, 'id' | 'createdAt'>);
  }
};
